import config from "appConfig";

import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0302 from "../lesson-02/lesson";
import lesson0303 from "../lesson-03/lesson";
const linkLesson0302 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0302.id);
const linkLesson0303 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0303.id);

const theory = `<p>Fourier analysis (also called frequency analysis or spectral analysis) consists in analyzing the information of continuous time signals in terms of frequency distribution. It is related to the idea suggested by Fourier that any function can be decomposed into a sum of sinusoidal functions.<br class=newline>
<h3 class=sectionHead>Fourier Transform</h3>
<p>
     Fourier Transform is an integral transform. For a given frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>F</mi>
   </math>
   , and a time domain signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   , it is defined as:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>∞</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi>∞</mi>
               </mrow>
            </msubsup>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mi>F</mi>
                  <mi>t</mi>
               </mrow>
            </msup>
            <mi>d</mi>
            <mi>t</mi>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>X</mi>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>F</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   is a complex number. Its modulus represents how much of the frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>F</mi>
   </math>
   is present in the original signal and the argument represents the phase shift. For some applications, the knowledge of the modulus is sufficient (noise control for example). Knowledge of the phase is, on the other side, mandatory for sound synthesis for instance. The Fourier Transform is defined for positive and negative frequencies
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>F</mi>
   </math>
   . If negative frequencies have no physical sense, their existence is linked to the definition of the FT.<br class=newline>
<p>Fourier transform is reversible: it is possible to come back to the time domain signal through the Inverse Fourier Transform (IFT), defined as:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>∞</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi>∞</mi>
               </mrow>
            </msubsup>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mi>t</mi>
                  <mi>F</mi>
               </mrow>
            </msup>
            <mi>d</mi>
            <mi>F</mi>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   It means that a same physical quantity may be represented in the time domain or in the Fourier domain. This requires some properties on
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   in term of regularity but we will consider that they are satisfied for physical signals. Fourier Transform can be represented as:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>One key feature of the Fourier transform is <strong><span class=cmbx-10>convolution property</span></strong> that states that convolution in time corresponds to multiplication in the frequency domain:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>∗</mo>
            <mi>y</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>⋅</mo>
            <mi>Y</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>On the other side, multiplying in the time domain, often called windowing, is equivalent to convolute in the Fourier domain:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>⋅</mo>
            <mi>w</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>∗</mo>
            <mi>W</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>This suggest that the response of a system can be determined without computing a convolution integral. This method will be presented in section 3.
<h3 class=sectionHead>Properties of the FT</h3>
<p>In this section, properties of the Fourier Transform are presented. As a sake of illustration, we will present them through the example of the FT associated to the rectangular window. Let:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>w</mi>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </msub>
            <mi>t</mi>
            <mo class=MathClass-rel>↦</mo>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mn>1</mn>
                           <mstyle class=text>
                              <mtext class=textrm mathvariant=normal> if </mtext>
                           </mstyle>
                           <mrow>
                              <mo fence=true form=prefix>|</mo>
                              <mrow>
                                 <mi>t</mi>
                              </mrow>
                              <mo fence=true form=postfix>|</mo>
                           </mrow>
                           <mo class=MathClass-rel>≤</mo>
                           <mi>T</mi>
                           <mo class=MathClass-bin>∕</mo>
                           <mn>2</mn>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mn>0</mn>
                           <mstyle class=text>
                              <mtext class=textrm mathvariant=normal> if </mtext>
                           </mstyle>
                           <mrow>
                              <mo fence=true form=prefix>|</mo>
                              <mrow>
                                 <mi>t</mi>
                              </mrow>
                              <mo fence=true form=postfix>|</mo>
                           </mrow>
                           <mo class=MathClass-rel>></mo>
                           <mi>T</mi>
                           <mo class=MathClass-bin>∕</mo>
                           <mn>2</mn>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(6)
</table>
<p>Its Fourier Transform is:
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>w</mi>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <msub>
               <mrow>
                  <mi>W</mi>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>π</mi>
                  <mi>F</mi>
                  <mi>T</mi>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mrow>
                  <mi>π</mi>
                  <mi>F</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mi>T</mi>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>sinc</mtext>
            </mstyle>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>π</mi>
            <mi>T</mi>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   This is a sinus cardinal function. As all the Fourier Transforms, it is a continuous function and its limit toward plus and minus infinity are zero. In the present case, the function is purely real and has no imaginary part. This is due to the parity of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>w</mi>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msub>
   </math>
   . It is the occasion to see what is the influence of a delay on the time signal. We have the property:
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>y</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mi>Y</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mi>F</mi>
                  <mi>τ</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(8)
</table>
<p>This means that a time delay acts as a modification of only the phase of the signal. This can be understood as signal have the same spectral content and the delay acts as a phase shift.<br class=newline>
<p>
   Another interesting property is time dilatation. Let
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>a</mi>
      <mo class=MathClass-rel>∈</mo>
      <msup>
         <mrow>
            <mi>ℝ</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
            <mo class=MathClass-bin>∗</mo>
         </mrow>
      </msup>
   </math>
   , we have:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>a</mi>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </mfrac>
            <mi>X</mi>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>F</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(9)
</table>
<p>A concrete feature: when you here a musical track that runs faster, it seems higher pitched. Concerning the rectangular window, we have:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>w</mi>
               </mrow>
               <mrow>
                  <mi>T</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>w</mi>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>a</mi>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(10)
</table>
<p>Hence,
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>W</mi>
               </mrow>
               <mrow>
                  <mi>T</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </mfrac>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>sinc</mtext>
            </mstyle>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>π</mi>
            <mi>a</mi>
            <mi>T</mi>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   This leads to an interesting particular case. When
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>a</mi>
      <mo class=MathClass-rel>→</mo>
      <mn>0</mn>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>a</mi>
      <mo class=MathClass-rel>→</mo>
      <mo class=MathClass-bin>+</mo>
      <mi>∞</mi>
   </math>
   so that the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>w</mi>
         </mrow>
         <mrow>
            <mi>T</mi>
            <mo class=MathClass-bin>∕</mo>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   function goes to the constant function equal to unity. Concerning the Fourier transform its value at zero goes to infinity and the width of the sinus cardinal function goes to zero so that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>W</mi>
         </mrow>
         <mrow>
            <mi>T</mi>
            <mo class=MathClass-bin>∕</mo>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   converges to the Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   . This leads to a key property:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mn>1</mn>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mi>δ</mi>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   Let’s now take
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   , we have:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>a</mi>
            <msub>
               <mrow>
                  <mi>w</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>∕</mo>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>sinc</mtext>
            </mstyle>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>π</mi>
            <mi>a</mi>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(13)
</table>
<p>
   For
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>a</mi>
      <mo class=MathClass-rel>→</mo>
      <mo class=MathClass-bin>+</mo>
      <mi>∞</mi>
   </math>
   , the function
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>a</mi>
      <msub>
         <mrow>
            <mi>w</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mo class=MathClass-bin>∕</mo>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   converges to the Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   and the sinus cardinal has an infinite width and converges toward the constant function. Then we have:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>δ</mi>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mn>1</mn>
         </math>
      <td class=eq-no>(14)
</table>
<p>
   Since now, we have only considered that the time domain signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   is real. Nevertheless, definition (2) can be extended to complex signals, even if those are not physical. For complex signals, we have the spectral shift property, which is the counterpart of the time delay property:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>x</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mn>2</mn>
                  <mi>π</mi>
                  <msub>
                     <mrow>
                        <mi>F</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>t</mi>
               </mrow>
            </msup>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(15)
</table>
<p>This leads to a key result. The cosine function can be written as a sum of two complex exponential functions:
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mn>2</mn>
            <mi>π</mi>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                        <mn>2</mn>
                        <mi>π</mi>
                        <msub>
                           <mrow>
                              <mi>F</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mi>t</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi>j</mi>
                        <mn>2</mn>
                        <mi>π</mi>
                        <msub>
                           <mrow>
                              <mi>F</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mi>t</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(16)
</table>
<p>
   The Fourier transform of the first term is (consider
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   in (15))
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mn>2</mn>
                  <mi>π</mi>
                  <msub>
                     <mrow>
                        <mi>F</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>t</mi>
               </mrow>
            </msup>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(17)
</table>
<p>We then end with:
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mn>2</mn>
            <mi>π</mi>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mfrac>
               <mrow>
                  <mi>δ</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>f</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>F</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close stretchy=false>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <mi>δ</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>f</mi>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>F</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(18)
</table>
<p>
   The FT transform is then the sum of two delayed Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   . This highlights the role of negative frequencies.<br class=newline>Similarly
</p>
<table class=equation>
   <tr>
      <td style="font-size: 14px">
<math xmlns=http://www.w3.org/1998/Math/MathML display=block>
   <mtable columnalign=left class=align>
      <mtr>
         <mtd columnalign=right class=align-odd>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mn>2</mn>
            <mi>π</mi>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </mtd>
         <mtd class=align-even>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mfrac>
               <mrow>
                  <mi>δ</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>f</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>F</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close stretchy=false>)</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mi>δ</mi>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <mi>f</mi>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>F</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>j</mi>
               </mrow>
            </mfrac>
         </mtd>
      </mtr>
   </mtable>
</math>
<td class=eq-no>(19)
</table>
<p>
   Hence pure frequency signals are combinations of delayed Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   . They contain only one frequency!
<h3 class=sectionHead>Fourier transform and frequency response</h3>
<p>
   It has been shown in <a href="${linkLesson0302}" target="_blank">Lesson 3.2</a> that a LTI system can be characterised by its impulse response
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   and in <a href="${linkLesson0303}" target="_blank">Lesson 3.3</a> that the response of the system to an input signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   can be determined with a convolution. An alternative to the convolution integral is to use the convolution property (4).<br class=newline>
<p>
   First, the Fourier Transform of the impulse response
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
   </math>
   of a LTI system is called Frequency Response Function (FRF) and is defined as:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>H</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo>∫</mo>
            <mi>ℝ</mi>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mi>F</mi>
                  <mi>t</mi>
               </mrow>
            </msup>
            <mi>d</mi>
            <mi>t</mi>
         </math>
      <td class=eq-no>(20)
</table>
<p>
   Similarly to the impulse response, the frequency response completely defines the LTI system. In other words, the knowledge of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
   </math>
   or
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>H</mi>
   </math>
   are theoretically equivalent.<br class=newline>
<p>Convolution property (4) leads to:
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>y</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>x</mi>
            <mo class=MathClass-bin>∗</mo>
            <mi>h</mi>
            <mover class=stackrel>
               <mrow>
                  <mo class=MathClass-rel>↔</mo>
               </mrow>
               <mrow>
                  <mrow>
                     <mi>F</mi>
                     <mi>T</mi>
                  </mrow>
               </mrow>
            </mover>
            <mi>Y</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>H</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>⋅</mo>
            <mi>X</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(21)
</table>
<p>
   The multiplication of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>H</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>X</mi>
   </math>
   is pointwize which is more simple to perform than a convolution.<br class=newline>
<p>
   The general scheme to compute the response is then presented in <a href=# data-img-alt="Figure 1. Response of the system through frequency domain." data-img-src=${config.ASSETS_URL}/03/04/theory/figure-1.svg>Figure 1</a>.
<figure class=figure>
   <img alt="Figure 1. Response of the system through frequency domain." src=${config.ASSETS_URL}/03/04/theory/figure-1.svg>
  <figcaption class=caption>Figure 1. Response of the system through frequency domain.</figcaption>
</figure>
<p>
   In acoustics, the Fourier Transform of a source-receiver impulse response is called the acoustic Transfer Function (TF).
<p>   Let’s come back to the acoustic problem of <a href="${linkLesson0302}" target="_blank">Lesson 3.2</a>. The response at the receiver for an input signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
   </math>
   at the source was computed in <a href="${linkLesson0303}" target="_blank">Lesson 3.3</a>. We will now compute this response by the way of the Fourier Transform. The source-receiver impulse response was:
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>h</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(22)
</table>
<p>The Acoustic Transfer Function is then:
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>H</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mi>F</mi>
                  <msub>
                     <mrow>
                        <mi>τ</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mi>F</mi>
                  <msub>
                     <mrow>
                        <mi>τ</mi>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(23)
</table>
<p>
   The Fourier Transform
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Y</mi>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>F</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   of the response is the product of the Fourier Transform
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>F</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   of the input signal by the ATF
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>Y</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mi>S</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mi>F</mi>
                  <msub>
                     <mrow>
                        <mi>τ</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mi>S</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>F</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>j</mi>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mi>F</mi>
                  <msub>
                     <mrow>
                        <mi>τ</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(24)
</table>
<p>For each one of the two terms, the exponential is a phase shift which corresponds, after Inverse Fourier Transform, to a delay in the time domain. We then ends with the same expression (!) than the one obtained in <a href="${linkLesson0303}" target="_blank">Lesson 3.3</a>
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>y</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mi>s</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mi>s</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(25)
</table>

<h3 class=sectionHead>Glossary</h3>
<dl>
  <dt>LTI
  <dd>Linear Time-Invariant</dd>
    <dt>TF
  <dd>Transfer Function</dd>
</dl>`;

export default theory;
